import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalDialog,
  Stack,
  Typography,
} from '@mui/joy';
import { FaPlus, FaTrash } from 'react-icons/fa';
import { IconWrapper } from '../../components/IconWrapper';

type Row = { name: string; amount: string };

function formatMoney(value: number) {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value);
}

/** Agreed deductions on a reissued balance. Amounts include VAT, like the invoice total. */
export function BalanceDeductionsModal({
  open,
  invoiceRef,
  balanceBeforeDeductions,
  deductions,
  onClose,
  onSave,
}: {
  open: boolean;
  invoiceRef: string;
  /** The balance line as issued, before any deductions. */
  balanceBeforeDeductions: number;
  deductions: Array<{ name: string; amount: number }>;
  onClose: () => void;
  onSave: (deductions: Array<{ name: string; amount: number }>) => Promise<void>;
}) {
  const [rows, setRows] = useState<Row[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setRows(deductions.length
      ? deductions.map((item) => ({ name: item.name, amount: item.amount.toFixed(2) }))
      : [{ name: 'Agreed reduction', amount: '' }]);
    setError(null);
  }, [open, deductions]);

  const parsed = rows.map((row) => ({ name: row.name.trim(), amount: Math.abs(Number(row.amount) || 0) }));
  const deducted = parsed.reduce((sum, row) => sum + row.amount, 0);
  const newBalance = Math.round((balanceBeforeDeductions - deducted) * 100) / 100;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(parsed.filter((row) => row.amount > 0));
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save deductions');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={() => { if (!saving) onClose(); }}>
      <ModalDialog sx={{ width: 'min(520px, calc(100vw - 32px))' }}>
        <DialogTitle>Deductions · {invoiceRef}</DialogTitle>
        <DialogContent>
          <Stack gap={1.5}>
            <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
              Amounts include VAT. They come off this balance and off the agreed total for the job.
            </Typography>
            {rows.map((row, index) => (
              <Stack key={index} direction="row" gap={1} alignItems="flex-end">
                <FormControl sx={{ flex: 1 }}>
                  {index === 0 && <FormLabel>Description</FormLabel>}
                  <Input
                    value={row.name}
                    onChange={(event) => setRows((prev) => prev.map((item, i) => (i === index ? { ...item, name: event.target.value } : item)))}
                  />
                </FormControl>
                <FormControl sx={{ width: 130 }}>
                  {index === 0 && <FormLabel>Amount (£)</FormLabel>}
                  <Input
                    type="number"
                    slotProps={{ input: { min: 0, step: '0.01' } }}
                    value={row.amount}
                    onChange={(event) => setRows((prev) => prev.map((item, i) => (i === index ? { ...item, amount: event.target.value } : item)))}
                  />
                </FormControl>
                <IconButton
                  aria-label="Remove deduction"
                  variant="plain"
                  color="neutral"
                  onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}
                >
                  <IconWrapper icon={FaTrash} />
                </IconButton>
              </Stack>
            ))}
            <Button
              size="sm"
              variant="plain"
              startDecorator={<IconWrapper icon={FaPlus} />}
              sx={{ alignSelf: 'flex-start' }}
              onClick={() => setRows((prev) => [...prev, { name: 'Agreed reduction', amount: '' }])}
            >
              Add deduction
            </Button>
            <Stack direction="row" justifyContent="space-between">
              <Typography level="body-sm">Balance {formatMoney(balanceBeforeDeductions)} − {formatMoney(deducted)}</Typography>
              <Typography level="title-sm">{formatMoney(newBalance)}</Typography>
            </Stack>
            {error && <Alert color="danger">{error}</Alert>}
            <Stack direction="row" justifyContent="flex-end" gap={1}>
              <Button variant="plain" color="neutral" disabled={saving} onClick={onClose}>Cancel</Button>
              <Button loading={saving} disabled={newBalance <= 0} onClick={() => { void save(); }}>Save deductions</Button>
            </Stack>
          </Stack>
        </DialogContent>
      </ModalDialog>
    </Modal>
  );
}
